/**
 * This file is a utility file for the creation of address fields for forms.
 */

import { chain, drop, last, size } from 'lodash';

/**
 * Creates the keys/values for the address field objects returned in the
 * makeAddressValues and makeAddressNames functions
 * @param {string} field the name of the field
 * @param {boolean} payment whether the field is for billing address or not
 *
 * @returns {string} the name of the field
 */
const constructAddressField = (field, payment) => {
  const isAddress = field.match(/address/);
  return payment
    ? `${isAddress ? 'payment_billing' : 'payment'}_${field}`
    : field;
};

export function getAddressKey(name) {
  const isAddress = name.match(/address/);
  const nameArr = name.split('_');
  const dropLen = size(nameArr) > 2 ? 2 : 0;
  return isAddress ? drop(nameArr, dropLen).join('_') : last(nameArr);
}

/**
 * Generates hashmap of variables that point to property's values on the form
 * @param {object} form object with the field values from the vendor
 * @param {boolean} payment whether the address fields are for billing address
 *
 * @returns {object} returns an object of variables as keys and field values as values
 * example: {
 *  country: 'US',
 *  address: '100 main street'
 * }
 */
export function makeAddressValues(form, payment = false) {
  const fields = ['country', 'address', 'address_2', 'zip', 'city', 'state'];
  return chain(fields)
    .keyBy()
    .mapValues((f) => {
      const key = constructAddressField(f, payment);
      return form[key];
    })
    .value();
}

/**
 * Generates hashmap of variables that point to property names on the form
 * @param {boolean} payment whether the address fields are for billing address
 *
 * @returns {object} returns an object of variables as keys and field names as values
 * example: {
 *  country_field: 'country',
 *  address_field: 'address'
 * }
 */
export function makeAddressNames(payment = false) {
  const fields = ['country', 'address', 'address_2', 'zip', 'city', 'state'];
  return chain(fields)
    .keyBy((f) => `${f}_field`)
    .mapValues((key) => constructAddressField(key, payment))
    .value();
}
